<template>
  <button
    type="button"
    role="switch"
    class="toggle-button"
    :class="`toggle-button--${size}`"
    :aria-checked="checked"
    :aria-label="ariaLabel || null"
    :disabled="disabled"
    :id="id"
    @click="handleToggle"
  >
    <span class="toggle-track" :class="{ 'is-on': checked }">
      <span class="toggle-knob"></span>
    </span>
  </button>
</template>

<script>
export default {
  name: 'ToggleButton',
  emits: ['update:checked', 'trigger'],
  props: {
    // Controlled on/off state (v-model:checked). Drives aria-checked + knob position.
    checked: { type: Boolean, default: false },
    // Legacy initial-state prop — kept for the existing consumer contract; display is driven by `checked`.
    checkInit: { type: Boolean, default: false },
    id: { type: String, default: '' },
    // Accessible name for the switch (label / aria-label / aria-labelledby all supported;
    // aria-labelledby falls through to the root <button>).
    ariaLabel: { type: String, default: '' },
    // Native disabled → leaves the tab order.
    disabled: { type: Boolean, default: false },
    // small | default | large
    size: {
      type: String,
      default: 'default',
      validator: (v) => ['small', 'default', 'large'].includes(v)
    }
  },
  setup(props, { emit }) {
    const handleToggle = () => {
      emit('update:checked', !props.checked)
      emit('trigger')
    }
    return { handleToggle }
  },
}
</script>

<style lang="scss" scoped>
// v2 Toggle Switch — role=switch a11y contract (guideline "Toggle Switch", L241–262).
// Knob POSITION is the redundant non-colour state cue; fill colour is secondary.
// Reduced-motion: the global net in _global.scss (`*, *::before, *::after { transition-duration }`)
// already snaps the knob transition below — do NOT override it here.
.toggle-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  // ≥44×44 touch target (shared convention) — the visible track can be smaller.
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  @include focus-ring; // v2 GREEN ring (flips the old hand-rolled gold outline)
  &:disabled {
    cursor: not-allowed;
    .toggle-track {
      opacity: 0.4; // v2 disabled — 40%
    }
  }
}

.toggle-track {
  position: relative;
  display: inline-block;
  box-sizing: border-box;
  background-color: $border; // off track (v2 Border; W7-gate: flips #2E2E2E dark, was pinned $border-hairline)
  transition: background-color $dur-base $ease-out; // §11
  &.is-on {
    background-color: $brand-green; // on track (v2 #197660)
  }
}

.toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  background-color: white;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform $dur-base $ease-out; // §11 — snapped under prefers-reduced-motion
}

// On-state hover deepens the track + lifts the knob (guideline "On — hover").
.toggle-button:hover:not(:disabled) .toggle-track.is-on {
  background-color: $green-deep; // v2 Green Dark #0F5344
  .toggle-knob {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.28);
  }
}

// --- Sizes (exact px from the v2 guideline: default 44×24, small 32×18, large 56×32) ---
.toggle-button--default .toggle-track {
  width: 44px;
  height: 24px;
  border-radius: 12px;
  .toggle-knob {
    width: 20px;
    height: 20px;
  }
  &.is-on .toggle-knob {
    transform: translateX(20px);
  }
}

.toggle-button--small .toggle-track {
  width: 32px;
  height: 18px;
  border-radius: 9px;
  .toggle-knob {
    width: 14px;
    height: 14px;
  }
  &.is-on .toggle-knob {
    transform: translateX(14px);
  }
}

.toggle-button--large .toggle-track {
  width: 56px;
  height: 32px;
  border-radius: 16px;
  .toggle-knob {
    width: 28px;
    height: 28px;
  }
  &.is-on .toggle-knob {
    transform: translateX(24px);
  }
}
</style>
